import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent,
  FundraisingProgressTracker,
  FundraisingLeaderboard,
  FundraisingMetricsGrid,
  FunnelChart,
  DilutionChart,
  type LeaderboardItem,
} from "@repo/o37-ui";
import { TrendingUp, Users, FileText, DollarSign, Target, PieChart } from "lucide-react";
import { FundraisingFund, FundraisingAnalytics, FUND_STAGES, CapTableEntry, calculateTermSheetMetrics } from '../../types';

interface RaiseAnalyticsProps {
  raiseId: string;
  analytics: FundraisingAnalytics;
  funds: FundraisingFund[];
  capTable?: CapTableEntry[];
}

export default function RaiseAnalytics({ analytics, funds, capTable = [] }: RaiseAnalyticsProps) {
  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  // Get all term sheets from funds
  const allTermSheets = funds.flatMap(fund => 
    (fund.termSheets || []).map(ts => ({
      ...ts,
      fundName: fund.name
    }))
  ).sort((a, b) => (b.investmentAmount || 0) - (a.investmentAmount || 0));

  // Transform term sheets for the dilution simulator
  const termSheetData = allTermSheets
    .filter(ts => ts.preMoneyValuation) // Only include term sheets with valuation
    .map(ts => ({
      id: ts.id,
      fundName: ts.fundName,
      investmentAmount: ts.investmentAmount,
      preMoneyValuation: ts.preMoneyValuation!,
      status: ts.status,
    }));

  // Sort term sheets by efficiency ($ per % dilution) - higher is better
  // This rewards deals with high investment and low dilution
  const sortedTermSheets = [...allTermSheets]
    .map(ts => {
      const metrics = ts.preMoneyValuation 
        ? calculateTermSheetMetrics(ts.investmentAmount, ts.preMoneyValuation)
        : null;
      const efficiency = metrics && metrics.dilutionPercentage > 0 
        ? ts.investmentAmount / metrics.dilutionPercentage 
        : 0;
      return { ...ts, metrics, efficiency };
    })
    .sort((a, b) => b.efficiency - a.efficiency);

  // Transform term sheets for leaderboard
  const leaderboardItems: LeaderboardItem[] = sortedTermSheets.map((ts, index) => {
    return {
      id: ts.id,
      rank: index + 1,
      name: ts.fundName,
      subtitle: ts.preMoneyValuation ? `${formatCurrency(ts.preMoneyValuation)} valuation` : undefined,
      value: ts.investmentAmount,
      status: ts.status as LeaderboardItem['status'],
      details: [
        {
          label: "Valuation",
          value: ts.preMoneyValuation ? formatCurrency(ts.preMoneyValuation) : "-"
        },
        {
          label: "Dilution",
          value: ts.metrics ? `${ts.metrics.dilutionPercentage.toFixed(2)}%` : "-"
        },
        {
          label: "Efficiency",
          value: ts.metrics ? `${formatCurrency(ts.efficiency)} / 1%` : "-"
        }
      ]
    };
  });

  // Transform for funnel chart
  const funnelSteps = FUND_STAGES.filter(s => s.stage !== 'uninterested').map(({ stage, label, color, description }) => ({
    id: stage,
    label,
    value: analytics.stageCounts[stage] || 0,
    colorClassName: color,
    description,
  }));

  // Calculate total dilution from term sheets (compounded)
  const termSheetsWithValuation = allTermSheets.filter(ts => ts.preMoneyValuation);
  
  let totalDilution = 0;
  if (termSheetsWithValuation.length > 0) {
    termSheetsWithValuation.forEach(ts => {
      const metrics = calculateTermSheetMetrics(ts.investmentAmount, ts.preMoneyValuation!);
      // Compounded dilution: total = total + (1 - total/100) * new_dilution
      totalDilution = totalDilution + (1 - totalDilution / 100) * metrics.dilutionPercentage;
    });
  }

  // Key metrics for the top grid
  const metrics = [
    {
      label: "Total Funds",
      value: analytics.totalFunds.toString(),
      hint: `${analytics.activeFunds} in active stages`,
      icon: <Users className="w-3.5 h-3.5" />,
    },
    {
      label: "Pipeline Value",
      value: formatCurrency(analytics.pendingCapital),
      hint: "From pending term sheets",
      icon: <Target className="w-3.5 h-3.5" />,
    },
    {
      label: "Committed Capital",
      value: formatCurrency(analytics.committedCapital),
      hint: `${analytics.totalTermSheets} term sheets`,
      icon: <DollarSign className="w-3.5 h-3.5" />,
    },
    {
      label: "Dilution",
      value: termSheetsWithValuation.length > 0 
        ? `${totalDilution.toFixed(1)}%` 
        : "N/A",
      hint: "Total from term sheets",
      icon: <PieChart className="w-3.5 h-3.5" />,
    },
    {
      label: "Subscription",
      value: `${analytics.oversubscriptionPct}%`,
      hint: `Goal: ${formatCurrency(analytics.raiseGoal)}`,
      icon: <TrendingUp className="w-3.5 h-3.5" />,
      colorClassName: analytics.oversubscriptionPct >= 100 ? "text-emerald-600" : "text-neutral-900",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <FundraisingMetricsGrid metrics={metrics} columns={5} />

      {/* Progress Tracker */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Progress to Goal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <FundraisingProgressTracker
            current={analytics.committedCapital}
            goal={analytics.raiseGoal}
            pending={analytics.pendingCapital}
            formatCurrency={formatCurrency}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dilution Simulator */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Dilution Simulator
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <DilutionChart
              capTable={capTable}
              termSheets={termSheetData}
              formatCurrency={formatCurrency}
            />
          </CardContent>
        </Card>

        {/* Term Sheet Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Term Sheet Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FundraisingLeaderboard
              items={leaderboardItems}
              formatValue={formatCurrency}
              emptyMessage="No term sheets received yet"
              maxItems={5}
            />
          </CardContent>
        </Card>
      </div>

      {/* Conversion Funnel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Conversion Funnel
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <FunnelChart steps={funnelSteps} height={300} showLegend />
        </CardContent>
      </Card>
    </div>
  );
}
